import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

class ReadModelHub {
  constructor() {
    this.connection = new HubConnectionBuilder()
      .withUrl(process.env.REACT_APP_API_URL + '/readmodelhub', {
        withCredentials: true
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          if (retryContext.previousRetryCount === 0) {
            return 0;
          }
          const maxDelay = 30000;
          return Math.min(maxDelay, 1000 * Math.pow(2, retryContext.previousRetryCount));
        }
      })
      .build();

    this.subscriptions = [];
    this.subscriptionsToData = [];
    this.keepAliveInterval = null;

    // Add reconnection event handlers
    this.connection.onreconnecting(error => {
      console.log('SignalR reconnecting due to error:', error);
    });

    this.connection.onreconnected(connectionId => {
      console.log('SignalR reconnected with connectionId:', connectionId);
      this.resubscribeAll();
    });

    this.connection.onclose(error => {
      console.log('SignalR connection closed with error:', error);
      if (this.keepAliveInterval) {
        clearInterval(this.keepAliveInterval);
        this.keepAliveInterval = null;
      }
      
      // Try to restart the connection
      setTimeout(() => this.start(), 5000);
    });

    // Add event listeners
    this.connection.on('modelUpdated', this.handleModelUpdated.bind(this));
    this.connection.on('sendToClient', this.handleDataReceived.bind(this));

    // Start the connection when the object is instantiated
    this.start();
  }

  async start() {
    if (this.connection.state === HubConnectionState.Disconnected) {
      try {
        console.log("ReadModelHub - Starting connection to SignalR...");
        await this.connection.start();
        console.log("ReadModelHub - Successfully connected to SignalR");
        
        // Start keep-alive
        this.startKeepAliveInterval();
        
        // Resubscribe if needed
        if (this.subscriptions.length > 0 || this.subscriptionsToData.length > 0) {
          this.resubscribeAll();
        }
      } catch (error) {
        console.error("ReadModelHub - Connection error:", error);
        
        // Try to reconnect after a delay
        setTimeout(() => this.start(), 5000);
      }
    } else {
      console.log(`ReadModelHub - Connection is already in ${this.connection.state} state`);
    }
  }

  startKeepAliveInterval() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
    }
    
    this.keepAliveInterval = setInterval(() => {
      if (this.connection.state === HubConnectionState.Connected) {
        this.connection.invoke("KeepAlive").catch(err => {
          console.warn("Error sending keep-alive ping:", err);
        });
      }
    }, 15000);
  }

  resubscribeAll() {
    console.log('Resubscribing to all SignalR subscriptions');
    
    this.subscriptions.forEach(subscription => {
      this.invokeSubscription('subscribeToModel', 
        subscription.modelName, 
        subscription.notificationId
      );
    });
    
    this.subscriptionsToData.forEach(subscription => {
      this.invokeSubscription('subscribeToData', 
        subscription.dataType, 
        subscription.dataId
      );
    });
  }

  stop() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
    
    this.connection.stop();
  }

  handleModelUpdated(modelName, modelId) {
    console.log('ReadModelHub - modelUpdated information');
    this.subscriptions.forEach((subscription) => {
      if (
        subscription.modelName === modelName &&
        (!subscription.notificationId || subscription.notificationId === modelId)
      ) {
        this.handleSubscriptionAction(subscription);
      }
    });
  }

  handleDataReceived(dataType, dataId, data) {
    console.log('🔍 === DETAILED DEBUG START ===');
    console.log('🔍 dataType:', `"${dataType}"`);
    console.log('🔍 dataId:', `"${dataId}"`);
    console.log('🔍 data type:', typeof data);
    console.log('🔍 data value:', data);
    console.log('🔍 subscriptionsToData length:', this.subscriptionsToData.length);
    console.log('🔍 subscriptionsToData:', JSON.stringify(this.subscriptionsToData, null, 2));
    
    let matchFound = false;
    
    this.subscriptionsToData.forEach((subscription, index) => {
      console.log(`🔍 --- Checking subscription ${index} ---`);
      console.log(`🔍 subscription.dataType: "${subscription.dataType}"`);
      console.log(`🔍 subscription.dataId: "${subscription.dataId}"`);
      console.log(`🔍 dataType === subscription.dataType:`, dataType === subscription.dataType);
      console.log(`🔍 dataId === subscription.dataId:`, dataId === subscription.dataId);
      console.log(`🔍 !subscription.dataId:`, !subscription.dataId);
      console.log(`🔍 subscription.dataId === dataId:`, subscription.dataId === dataId);
      
      const dataTypeMatches = subscription.dataType === dataType;
      const dataIdMatches = (!subscription.dataId || subscription.dataId === dataId);
      
      console.log(`🔍 dataTypeMatches: ${dataTypeMatches}`);
      console.log(`🔍 dataIdMatches: ${dataIdMatches}`);
      console.log(`🔍 Overall match: ${dataTypeMatches && dataIdMatches}`);
      
      if (dataTypeMatches && dataIdMatches) {
        matchFound = true;
        console.log('✅ SUBSCRIPTION MATCHED! Executing action...');
        console.log('🔍 subscription.action type:', typeof subscription.action);
        console.log('🔍 subscription.dataId:', subscription.dataId);
        console.log('🔍 subscription.delay:', subscription.delay);
        
        // Handle the action execution specifically for data subscriptions
        if (subscription.dataId && !subscription.delay) {
          console.log('🚀 Executing action immediately');
          try {
            // Your backend sends data as a JSON string according to the interface
            const parsedData = data ? JSON.parse(data) : undefined;
            console.log('🔍 Parsed data:', parsedData);
            console.log('🔍 About to call subscription.action with parsed data');
            subscription.action(parsedData);
            console.log('✅ Action executed successfully');
          } catch (error) {
            console.error('❌ Error executing data subscription action:', error);
            console.error('❌ Raw data that caused error:', data);
            console.error('❌ Error stack:', error.stack);
          }
        } else {
          console.log('🕐 Executing action with delay:', subscription.delay);
          // Handle delayed execution
          if (!subscription.pendingAction) {
            subscription.pendingAction = setTimeout(() => {
              try {
                const parsedData = data ? JSON.parse(data) : undefined;
                console.log('🔍 Delayed execution - parsed data:', parsedData);
                subscription.action(parsedData);
                console.log('✅ Delayed action executed successfully');
              } catch (error) {
                console.error('❌ Error executing delayed data subscription action:', error);
              }
              subscription.pendingAction = null;
            }, subscription.delay || 0);
          } else {
            console.log('⏰ Clearing and resetting timeout');
            clearTimeout(subscription.pendingAction);
            subscription.pendingAction = setTimeout(() => {
              try {
                const parsedData = data ? JSON.parse(data) : undefined;
                subscription.action(parsedData);
              } catch (error) {
                console.error('❌ Error executing reset delayed data subscription action:', error);
              }
              subscription.pendingAction = null;
            }, subscription.delay || 0);
          }
        }
      } else {
        console.log('❌ Subscription not matched');
      }
    });
    
    if (!matchFound) {
      console.log('🚨 NO MATCHING SUBSCRIPTIONS FOUND!');
    }
    
    console.log('🔍 === DETAILED DEBUG END ===');
  }

  handleSubscriptionAction(subscription, data) {
    // Helper function to execute all actions in the subscription
    const executeActions = () => {
      const parsedData = data ? JSON.parse(data) : undefined;
      
      if (Array.isArray(subscription.actions)) {
        subscription.actions.forEach(action => {
          try {
            action(parsedData);
          } catch (error) {
            console.error("Error executing subscription action:", error);
          }
        });
      } else {
        console.warn("Subscription actions is not an array:", subscription);
      }
    };

    // Execute immediately or with delay based on subscription settings
    if (subscription.dataId && !subscription.delay) {
      executeActions();
    } else {
      if (!subscription.pendingAction) {
        subscription.pendingAction = setTimeout(() => {
          executeActions();
          subscription.pendingAction = null;
        }, subscription.delay);
      } else {
        clearTimeout(subscription.pendingAction);
        subscription.pendingAction = setTimeout(() => {
          executeActions();
          subscription.pendingAction = null;
        }, subscription.delay);
      }
    }
  }

  addSubscription(subscriptionsArray, modelName, notificationId, action, delay) {
    const existingSubscription = subscriptionsArray.find(subscription =>
      subscription.modelName === modelName && subscription.notificationId === notificationId);

    if (existingSubscription) {
      if (existingSubscription.actions.indexOf(action) !== -1) return;
      existingSubscription.actions.push(action);
    } else {
      console.log('ReadmodelHub - Adding subscription to modelName:', modelName, 'with notificationid:', notificationId);
      subscriptionsArray.push({
        modelName,
        notificationId,
        actions: [action],
        delay,
        pendingAction: null,
      });
    }
  }

  addSubscriptionToData(dataType, dataId, action, delay) {
    const existingSubscription = this.subscriptionsToData.find(
      (subscription) => subscription.dataType === dataType && subscription.dataId === dataId
    );
    
    console.log('ReadmodelHub - Adding subscription to dataType:', dataType, 'with dataId:', dataId);

    if (existingSubscription) {
      console.log('Subscription already exists, updating action');
      existingSubscription.action = action;
      return;
    }

    this.subscriptionsToData.push({
      dataType,
      dataId,
      action,
      delay: delay,
      pendingAction: null
    });
    
    console.log('Current subscriptionsToData after adding:', this.subscriptionsToData);
  }

  removeSubscription(subscriptionsArray, modelName, notificationId) {
    return subscriptionsArray.filter(subscription =>
      subscription.modelName !== modelName || subscription.notificationId !== notificationId);
  }

  removeSubscriptionToData(dataType, dataId) {
    const initialLength = this.subscriptionsToData.length;
    this.subscriptionsToData = this.subscriptionsToData.filter(
      (subscription) => subscription.dataType !== dataType || subscription.dataId !== dataId
    );
    console.log(`Removed ${initialLength - this.subscriptionsToData.length} data subscriptions for dataType: ${dataType}, dataId: ${dataId}`);
  }

  subscribe(modelName, notificationId, action, delay) {
    this.addSubscription(this.subscriptions, modelName, notificationId, action, delay);
    this.invokeSubscription('subscribeToModel', modelName, notificationId);
  }

  unsubscribe(modelName, notificationId) {
    this.subscriptions = this.removeSubscription(this.subscriptions, modelName, notificationId);
    this.invokeSubscription('unsubscribeFromModel', modelName, notificationId);
  }

  subscribeToData(dataType, dataId, action, delay) {
    this.addSubscriptionToData(dataType, dataId, action, delay);
    this.invokeSubscription('subscribeToData', dataType, dataId);
  }

  unsubscribeToData(dataType, dataId) {
    this.removeSubscriptionToData(dataType, dataId);
    this.invokeSubscription('unsubscribeFromData', dataType, dataId);
  }

  invokeSubscription(methodName, ...args) {
    if (this.connection.state === HubConnectionState.Connected) {
      this.connection.invoke(methodName, ...args)
        .catch(error => console.log(`${methodName} error: ${error}`));
    } else {
      console.log(`ReadModelHub - ${methodName} failed: Connection is not in 'Connected' state (current state: ${this.connection.state})`);
      
      // Store the invocation for when we reconnect
      if (methodName.includes('subscribe')) {
        // Don't need to queue unsubscribe calls when not connected
        if (!methodName.includes('unsubscribe')) {
          console.log(`ReadModelHub - Will invoke ${methodName} after reconnection`);
        }
      }
    }
  }

  connect() {
    this.start();
  }
}

export default new ReadModelHub();